import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HexColorPicker } from "react-colorful";
import { cn } from '@/lib/utils';
import FontDropdown from '../../ui/FontDropdown';
import GenericDropdown from '../../ui/GenericDropdown';
import { IconPicker } from '../../ui/heroicons-picker/IconPicker';
import { CustomizationTabProps } from './types';
import { Switch } from '@/components/ui/switch';
import { useUser } from '../../user/UserContext';
import { Badge } from '@/components/ui/badge';
import {
  Palette,
  MessageCircle,
  Lock,
  X,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AgentTabHeader from './AgentTabHeader';
import { ReactNode } from 'react';

function AppearanceOption({
  selected,
  onSelect,
  title,
  description,
  preview,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  preview: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex min-w-0 flex-col rounded-lg border bg-card p-3 text-left transition-colors',
        selected
          ? 'border-foreground/20 bg-muted/20'
          : 'border-border/70 hover:border-foreground/15'
      )}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium">{title}</h4>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
        </div>
        <span
          className={cn(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            selected ? 'border-primary' : 'border-muted-foreground/40'
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
      </div>
      <div className="mt-3 w-full">{preview}</div>
    </button>
  );
}

export default function AppearanceTab({
  color,
  setColor,
  endColor,
  setEndColor,
  colorScheme,
  setColorScheme,
  font,
  setFont,
  file,
  setFile,
  previewUrl,
  setPreviewUrl,
  setIconFile,
  iconPreviewUrl,
  setIconPreviewUrl,
  agentIconType,
  setAgentIconType,
  agentIcon,
  setAgentIcon,
  setFileChanged,
  setIconFileChanged,
  hideAimdoc,
  setHideAimdoc,
  widgetPosition,
  setWidgetPosition,
  collapsedMode,
  setCollapsedMode,
  expandedMode,
  setExpandedMode,
  darkModeEnabled,
  setDarkModeEnabled,
  darkModeDefault,
  setDarkModeDefault,
  darkModeColor,
  setDarkModeColor,
}: CustomizationTabProps & {
  collapsedMode: 'chat' | 'copilot';
  setCollapsedMode: (value: 'chat' | 'copilot') => void;
  expandedMode: 'chat' | 'copilot' | 'sidebar';
  setExpandedMode: (value: 'chat' | 'copilot' | 'sidebar') => void;
}) {
  const { checkLicenseType } = useUser();

  const handleLogoFile = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
      setFileChanged(true);
    }
  };

  const handleIconFile = (event: any) => {
    const iconFile = event.target.files[0];
    if (iconFile) {
      setIconFile(iconFile);
      setIconFileChanged(true);
    }
  };

  const clearLogo = () => {
    setFile(null);
    setPreviewUrl('');
    setFileChanged(true);
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreviewUrl('');
    setIconFileChanged(true);
  };

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={Palette}
        title="Appearance"
        description="Customize branding, colors, icons, and layout for your agent widget."
      />

      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
      {/* Branding Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Branding</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Set the logo, typography, and colors used throughout the widget.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card divide-y">
        {/* Logo */}
        <div className="p-4">
            <div className="min-w-0 space-y-3">
              <div>
                <Label className="text-sm font-medium">Logo</Label>
                <p className="mt-1 text-xs text-muted-foreground">Upload your brand logo for the agent header.</p>
              </div>
              {previewUrl ? (
                <div className="inline-flex items-center gap-3 rounded-lg border bg-background p-3">
                  <img
                    src={previewUrl}
                    alt="Logo preview"
                    className="max-w-[160px] max-h-[80px] rounded object-contain"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={clearLogo}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center gap-2 rounded-lg border border-dashed p-3 cursor-pointer hover:border-foreground/30 transition-colors">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Choose file...</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoFile}
                    className="hidden"
                  />
                </label>
              )}
            </div>
        </div>

        {/* Font & Color Scheme */}
        <div className="grid grid-cols-1 gap-5 p-4 @xl:grid-cols-2 @xl:gap-8">
          <div className="min-w-0 space-y-2">
                <Label className="text-sm font-medium">Font</Label>
                <FontDropdown value={font} setValue={setFont} />
          </div>

          <div className="min-w-0 space-y-2">
                <Label className="text-sm font-medium">Color scheme</Label>
                <GenericDropdown value={colorScheme} selectValues={["Solid", "Gradient"]} setValue={setColorScheme} defaultValue={'Solid'} />
          </div>
        </div>

        {/* Colors */}
        <div className={cn(
          "grid gap-5 p-4 @xl:gap-8",
          colorScheme === "Gradient" ? "grid-cols-1 @xl:grid-cols-2" : "grid-cols-1"
        )}>
          <div className="min-w-0 space-y-3">
                <div>
                  <Label className="text-sm font-medium">Primary color</Label>
                  <p className="mt-1 text-xs text-muted-foreground">Main brand color for the widget.</p>
                </div>
                <div className="flex flex-col items-start gap-3">
                  <HexColorPicker color={color} onChange={setColor} />
                  <Input
                    type="text"
                    className="w-32"
                    value={color}
                    onChange={(e) => {
                      let newValue = e.target.value;
                      if (newValue && !newValue.startsWith('#')) {
                        newValue = '#' + newValue;
                      }
                      setColor(newValue);
                    }}
                  />
                </div>
          </div>

          {colorScheme === "Gradient" && (
            <div className="min-w-0 space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Gradient end color</Label>
                    <p className="mt-1 text-xs text-muted-foreground">Secondary color for the gradient effect.</p>
                  </div>
                  <div className="flex flex-col items-start gap-3">
                    <HexColorPicker color={endColor} onChange={setEndColor} />
                    <Input
                      type="text"
                      className="w-32"
                      value={endColor}
                      onChange={(e) => {
                        let newValue = e.target.value;
                        if (newValue && !newValue.startsWith('#')) {
                          newValue = '#' + newValue;
                        }
                        setEndColor(newValue);
                      }}
                    />
                  </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Widget Icon Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Widget icon</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose the icon or image visitors use to open the widget.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-4">
            <div className="min-w-0 space-y-3">
              <div>
                <Label className="text-sm font-medium">Icon type</Label>
              </div>
              <GenericDropdown value={agentIconType} selectValues={["Icon", "Image"]} setValue={setAgentIconType} defaultValue={'Icon'} />

              {agentIconType === "Icon" && (
                <div className="pt-1">
                  <IconPicker
                    onChange={setAgentIcon}
                    library={'solid'}
                    value={agentIcon}
                  />
                </div>
              )}

              {agentIconType === "Image" && (
                <div className="pt-1">
                  {iconPreviewUrl ? (
                    <div className="inline-flex items-center gap-3 rounded-lg border bg-background p-3">
                      <img
                        src={iconPreviewUrl}
                        alt="Icon preview"
                        className="w-12 h-12 rounded-lg object-contain"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={clearIcon}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 rounded-lg border border-dashed p-3 cursor-pointer hover:border-foreground/30 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose icon image...</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleIconFile}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Position Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Position</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose which side of the page anchors the widget.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">
          <AppearanceOption
            selected={widgetPosition === 'left'}
            onSelect={() => setWidgetPosition('left')}
            title="Bottom left"
            description="Anchor the widget to the lower-left corner."
            preview={
              <div className="relative h-16 rounded-md border bg-muted/40">
                <div className="absolute bottom-2 left-2 h-6 w-6 rounded-full bg-primary" />
              </div>
            }
          />
          <AppearanceOption
            selected={widgetPosition === 'right'}
            onSelect={() => setWidgetPosition('right')}
            title="Bottom right"
            description="Anchor the widget to the lower-right corner."
            preview={
              <div className="relative h-16 rounded-md border bg-muted/40">
                <div className="absolute bottom-2 right-2 h-6 w-6 rounded-full bg-primary" />
              </div>
            }
          />
        </div>
      </div>

      {/* Collapsed Mode Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Collapsed mode</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose how the widget appears before a visitor opens it.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">
          <AppearanceOption
            selected={collapsedMode === 'chat'}
            onSelect={() => setCollapsedMode('chat')}
            title="Chat bubble"
            description="A compact corner button that opens the chat."
            preview={
              <div className="relative h-20 rounded-md border bg-muted/40">
                <div className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                  <MessageCircle className="h-4 w-4 text-primary-foreground" />
                </div>
              </div>
            }
          />
          <AppearanceOption
            selected={collapsedMode === 'copilot'}
            onSelect={() => setCollapsedMode('copilot')}
            title="Copilot bar"
            description="A centered input bar for a guided experience."
            preview={
              <div className="relative h-20 rounded-md border bg-muted/40">
                <div className="absolute bottom-3 left-1/2 flex w-4/5 -translate-x-1/2 items-center gap-2 rounded-md border bg-background p-2">
                  <div className="h-2 flex-1 rounded bg-muted" />
                  <div className="h-5 w-5 rounded-full bg-primary" />
                </div>
              </div>
            }
          />
        </div>
      </div>

      {/* Expanded Mode Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Expanded mode</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose the layout visitors see after opening the widget.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 @lg:grid-cols-3">
          {/* Chat Expanded Card */}
          <div
            className={cn(
              "group relative overflow-hidden rounded-lg border cursor-pointer transition-colors flex flex-col",
              expandedMode === 'chat'
                ? "border-foreground/20 bg-muted/20"
                : "border-border/70 hover:border-foreground/15"
            )}
            onClick={() => setExpandedMode('chat')}
          >
            <div className="p-3 flex flex-col flex-1">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <h4 className="text-sm font-medium">Chat window</h4>
                </div>
                <div className={cn(
                  "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                  expandedMode === 'chat'
                    ? "border-primary"
                    : "border-muted-foreground/40"
                )}>
                  {expandedMode === 'chat' && (
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-3">
                Traditional chat window that expands from the corner.
              </p>

              <div className="relative rounded-md border bg-muted/40 p-3 h-20 flex items-end justify-end">
                <div className="absolute top-2 left-2 right-2">
                  <div className="bg-background/80 backdrop-blur-sm rounded-lg p-2 shadow-sm max-w-[80%]">
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-3/4 mb-1"></div>
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="bg-primary/90 rounded-lg p-2 shadow-md max-w-[70%]">
                  <div className="h-1.5 bg-primary-foreground/80 rounded w-full mb-1"></div>
                  <div className="h-1.5 bg-primary-foreground/80 rounded w-2/3"></div>
                </div>
              </div>

            </div>
          </div>

          {/* Copilot Expanded Card */}
          <div className="relative flex flex-col">
            <div
              className={cn(
                "group relative overflow-hidden rounded-lg border transition-colors flex flex-col flex-1",
                expandedMode === 'copilot'
                  ? "border-foreground/20 bg-muted/20"
                  : "border-border/70 hover:border-foreground/15"
              )}
              onClick={() => setExpandedMode('copilot')}
            >
              <div className="p-3 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <h4 className="text-sm font-medium">Copilot modal</h4>
                  </div>
                  <div className={cn(
                    "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                    expandedMode === 'copilot'
                      ? "border-primary"
                      : "border-muted-foreground/40"
                  )}>
                    {expandedMode === 'copilot' && (
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mb-3">
                  Centered modal overlay for an immersive guided experience.
                </p>

                <div className="relative rounded-md border bg-muted/40 p-3 h-20 flex flex-col justify-center items-center">
                  <div className="relative w-[80%] bg-background border-2 border-primary/30 rounded-xl shadow-lg p-2">
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-3/4 mb-1"></div>
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-1/2 mb-1"></div>
                    <div className="h-4 bg-muted rounded-lg mt-1"></div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Sidebar Expanded Card */}
          <div className="relative flex flex-col">
            <div
              className={cn(
                "group relative overflow-hidden rounded-lg border transition-colors flex flex-col flex-1",
                expandedMode === 'sidebar'
                  ? "border-foreground/20 bg-muted/20"
                  : "border-border/70 hover:border-foreground/15"
              )}
              onClick={() => setExpandedMode('sidebar')}
            >
              <div className="p-3 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <h4 className="text-sm font-medium">Sidebar</h4>
                  </div>
                  <div className={cn(
                    "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                    expandedMode === 'sidebar'
                      ? "border-primary"
                      : "border-muted-foreground/40"
                  )}>
                    {expandedMode === 'sidebar' && (
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mb-3">
                  Full-height sidebar that pushes page content aside.
                </p>

                <div className="relative rounded-md border bg-muted/40 p-2 h-20 flex">
                  <div className="flex-1 bg-background/80 rounded-l-lg p-2">
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-3/4 mb-1.5"></div>
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-1/2 mb-1.5"></div>
                    <div className="h-1.5 bg-muted-foreground/20 rounded w-2/3"></div>
                  </div>
                  <div className="w-1/3 bg-background border-l-2 border-primary/30 rounded-r-lg p-2 shadow-lg">
                    <div className="flex items-center gap-1 mb-1.5">
                      <div className="w-3 h-3 rounded-full bg-muted-foreground/20"></div>
                      <div className="h-1 bg-muted-foreground/20 rounded flex-1"></div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1 bg-muted-foreground/10 rounded w-full"></div>
                      <div className="h-1 bg-muted-foreground/10 rounded w-3/4"></div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Removal Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Branding removal</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Control whether Aimdoc attribution appears in the widget.
          </p>
        </div>

        <div className={cn(
          'rounded-lg border bg-card p-4 transition-colors',
          hideAimdoc && checkLicenseType('pro') && 'border-foreground/10'
        )}>
          <div className="flex items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium">Hide Aimdoc logo</h4>
                {!checkLicenseType('pro') && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">Engage</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Remove Aimdoc branding from the agent widget.</p>
            </div>
            {checkLicenseType('pro') ? (
              <Switch
                checked={hideAimdoc}
                onCheckedChange={setHideAimdoc}
                className="shrink-0"
              />
            ) : (
              <Lock className="h-4 w-4 text-muted-foreground/40 shrink-0" />
            )}
          </div>
        </div>
      </div>

      {/* Dark Mode Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Dark mode</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Configure whether visitors can use a dark widget theme.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card divide-y">
          <div className="flex items-center justify-between gap-6 p-4">
              <div>
                <h4 className="text-sm font-medium">Enable dark mode</h4>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  Allow users to toggle between light and dark themes. When disabled, the widget always uses light mode.
                </p>
              </div>
            <Switch
              checked={darkModeEnabled}
              onCheckedChange={setDarkModeEnabled}
              className="shrink-0"
            />
          </div>

          {darkModeEnabled && (
            <>
              <div className="flex items-center justify-between gap-6 p-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Default theme</Label>
                  <p className="text-xs text-muted-foreground">
                    The theme applied on first load before the user overrides it.
                  </p>
                </div>
                <Select value={darkModeDefault} onValueChange={(v) => setDarkModeDefault(v as 'system' | 'light' | 'dark')}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    <div>
                      <Label className="text-sm font-medium">Dark mode color</Label>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        Choose the base dark color. Lighter shades for cards, borders, and text will be computed from this.
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-3">
                      <HexColorPicker color={darkModeColor} onChange={setDarkModeColor} />
                      <Input
                        type="text"
                        className="w-32"
                        value={darkModeColor}
                        onChange={(e) => {
                          let newValue = e.target.value;
                          if (newValue && !newValue.startsWith('#')) {
                            newValue = '#' + newValue;
                          }
                          setDarkModeColor(newValue);
                        }}
                      />
                    </div>
                  </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
